import { useState, useEffect } from 'react'
import './Footer.css'
import { FaLinkedin, FaGithub, FaEnvelope, FaPhoneAlt, FaChevronUp, FaCode, FaWhatsapp, FaDownload } from 'react-icons/fa'
import { SiLeetcode, SiGeeksforgeeks } from 'react-icons/si'
import { motion } from 'framer-motion'
import { useDevMode } from './DeveloperMode'
import { Link } from '../lib/router'
import { LINKS } from '../config/links'
import { openAppWithFallback } from '../utils/appLinks'
import { usePWAInstall } from '../hooks/usePWAInstall'

/** Footer with brand info, copy-to-clipboard contact details, social profile links, scroll-to-top button, and scroll-triggered entrance animations. */
const Footer = () => {
  const { unlocked } = useDevMode();
  const { canInstall, isInstalled, install } = usePWAInstall();
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [copiedPhone, setCopiedPhone] = useState(false);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' ? window.innerWidth <= 768 : false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  /** Copies email to clipboard and shows a temporary "Copied!" tooltip for 1.5 seconds.
   * KNOWN GAP: navigator.clipboard.writeText() is NOT wrapped in try/catch.
   * If the clipboard API fails (non-HTTPS, permissions denied, or blocked by
   * browser), this throws an unhandled promise rejection. If clicking email
   * shows no "Copied!" feedback, check the console for clipboard errors. */
  const handleEmailClick = () => {
    navigator.clipboard.writeText('cs.mohitaroraofficial@gmail.com');
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 1500);
  };

  /** Copies phone number to clipboard and shows a temporary "Copied!" tooltip for 1.5 seconds.
   * Same KNOWN GAP as handleEmailClick -- unhandled promise rejection if
   * clipboard API is unavailable. */
  const handlePhoneClick = () => {
    navigator.clipboard.writeText('+919041158436');
    setCopiedPhone(true);
    setTimeout(() => setCopiedPhone(false), 1500);
  };

  /** Smooth-scrolls the window to the top of the page.
   * POTENTIAL CONFLICT: This could compete with App.tsx's hash-scroll polling
   * system. If the user clicks scroll-to-top while a hash-scroll animation is
   * in progress, two scroll commands may fight each other. The hash-scroll's
   * userInteracted flag would cancel the hash scroll, but the smooth scroll
   * from this function may also be interrupted by the ongoing animation. */
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="footer">
      <div className="container">
        {/* Scroll-triggered entrance: footer-top fades up when it enters the viewport. */}
        <motion.div 
          className="footer-top"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          {/* Brand column: logo and tagline. */}
          <div className="footer-brand">
            <h3 className="footer-logo">Mohit Arora<span>_</span></h3>
            <p className="footer-tagline">
              Senior Software Engineer architecting high-performance backend systems 
              and intelligent digital experiences.
            </p>
          </div>
          
          {/* Contact column with copy-to-clipboard email and phone. */}
          <div className="footer-contact">
            <h4>Contact Info</h4>
            <div className="contact-links">
              <div className="footer-contact-item-wrapper">
                <a href="mailto:cs.mohitaroraofficial@gmail.com" onClick={handleEmailClick} className="footer-contact-link">
                  <span className="contact-icon-wrapper"><FaEnvelope /></span>
                  <span className="contact-text">cs.mohitaroraofficial@gmail.com</span>
                </a>
                <span className={`footer-copied-tooltip ${copiedEmail ? 'visible' : ''}`}>
                  Copied!
                </span>
              </div>
              
              <div className="footer-contact-phone-whatsapp">
                <div className="footer-contact-item-wrapper">
                  <a href="tel:+919041158436" onClick={handlePhoneClick} className="footer-contact-link">
                    <span className="contact-icon-wrapper"><FaPhoneAlt /></span>
                    <span className="contact-text">(+91) 9041158436</span>
                  </a>
                  <span className={`footer-copied-tooltip ${copiedPhone ? 'visible' : ''}`}>
                    Copied!
                  </span>
                </div>
                <a
                  href="https://wa.me/919041158436"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-whatsapp-link"
                  title="Message on WhatsApp"
                  aria-label="Message on WhatsApp"
                  onClick={(e) => {
                    e.preventDefault();
                    openAppWithFallback("whatsapp://send?phone=919041158436", "https://wa.me/919041158436");
                  }}
                >
                  <FaWhatsapp />
                </a>
              </div>
            </div>
          </div>
          
          {/* Social profiles column: LinkedIn, GitHub, LeetCode, GeeksForGeeks, TakeUForward. */}
          <div className="footer-links-column">
            <h4>Profiles</h4>
            <div className="footer-links-grid">
              <a
                href={LINKS.LINKEDIN}
                target="_blank"
                rel="noopener noreferrer"
                data-cursor-tooltip="LinkedIn"
                aria-label="LinkedIn Profile"
                className="footer-links-btn"
                onClick={(e) => {
                  e.preventDefault();
                  openAppWithFallback("linkedin://profile/in/arora-sir", LINKS.LINKEDIN);
                }}
              >
                <FaLinkedin aria-hidden="true" />
              </a>
              <a href={LINKS.GITHUB} target="_blank" rel="noopener noreferrer" data-cursor-tooltip="GitHub" aria-label="GitHub Profile" className="footer-links-btn"><FaGithub aria-hidden="true" /></a>
              <a href={LINKS.LEETCODE} target="_blank" rel="noopener noreferrer" data-cursor-tooltip="LeetCode" aria-label="LeetCode Profile" className="footer-links-btn"><SiLeetcode aria-hidden="true" /></a>
              <a href={LINKS.GEEKSFORGEEKS} target="_blank" rel="noopener noreferrer" data-cursor-tooltip="GeeksForGeeks" aria-label="GeeksForGeeks Profile" className="footer-links-btn"><SiGeeksforgeeks aria-hidden="true" /></a>
              <a href={LINKS.TAKEU_FORWARD} target="_blank" rel="noopener noreferrer" data-cursor-tooltip="TakeUForward" aria-label="TakeUForward Profile" className="footer-links-btn"><FaCode aria-hidden="true" /></a>
              {canInstall && !isInstalled && (
                <button type="button" className="footer-install-btn" onClick={install} aria-label="Install App" data-cursor-tooltip="Install App">
                  <FaDownload aria-hidden="true" />
                </button>
              )}
            </div>
          </div>
        </motion.div>
        
        {/* Bottom bar: copyright, visitor counter (currently disabled), and scroll-to-top button. */}
        <motion.div 
          className="footer-bottom"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div className="footer-copy">
            <p>&copy; {new Date().getFullYear()} Mohit Arora. All rights reserved.</p>
            <p className="footer-legal">
              <Link href="/privacy">Privacy Policy</Link>
              <span className="footer-legal-sep">|</span>
              <Link href="/terms">Terms &amp; Conditions</Link>
              <span className="footer-legal-sep">|</span>
              <Link href="/#faq">FAQ</Link>
            </p>
            <p className="footer-hint">
              {unlocked
                ? (isMobile
                  ? "\u{1F6E1}\uFE0F Dev Mode active - close terminal to exit"
                  : "\u{1F6E1}\uFE0F Dev Mode active - Esc to close")
                : "\u{1F92B} MA hides a secret - find it"}
            </p>
          </div>
          
          {/* Visitor counter from komarev.com -- disabled.
           * If re-enabling, ensure the SVG badge styling (colors, font, border)
           * matches the footer theme. The badge URL below uses cyan accent and
           * dark background colors that match the site palette. */}
          {/* 
          <div className="visitor-counter">
            <img src="https://komarev.com/ghpvc/?username=arorasir-portfolio-website&label=WEBSITE%20VISITS&style=flat-square&color=00f0ff&label_color=0a1122" alt="Website Visitors" title="Total Website Visits" />
          </div>
          */}

          <button type="button" className="scroll-top" onClick={scrollToTop} aria-label="Scroll to top">
            <FaChevronUp />
          </button>
        </motion.div>
      </div>
    </footer>
  )
}

export default Footer
